/**
 * Every review in the organization, newest first, narrowed by stage — those
 * with gaps still open, those running, complete, failed — and by a search
 * over titles, documents and counterparties.
 *
 * @module components/apps/contractIntelligence/CiReviewsPage
 */
import { Link } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { useCiReviews, type CiReviewSummary } from "../../../state/contractIntelligence";
import { SegmentedTabs } from "../../regulation/PageHeader";
import { Button } from "../../ui/button";
import { isActive, openGapCount } from "./ciModel";
import { CiEmpty, CiListSkeleton, CiPage, CiSearch, CiUnavailable } from "./ciParts";
import { CiReviewRow } from "./CiReviewRow";
import { useCiOrg } from "./useCiOrg";

type Stage = "open" | "running" | "completed" | "failed" | "all";

const STAGE_TEST: Readonly<Record<Stage, (review: CiReviewSummary) => boolean>> = {
  open: (review) => openGapCount(review) > 0,
  running: (review) => isActive(review.status),
  completed: (review) => review.status === "completed",
  failed: (review) => review.status === "failed" || review.status === "draft",
  all: () => true,
};

const matches = (review: CiReviewSummary, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [review.title, ...review.documents.flatMap((document) => [document.name, document.counterparty ?? "", document.documentType ?? ""]), ...review.basis.items.map((item) => item.name)]
    .join(" ")
    .toLowerCase()
    .includes(needle);
};

export function CiReviewsPage() {
  const { organizationId, orgSegment } = useCiOrg();
  const reviews = useCiReviews(organizationId);
  const [stage, setStage] = useState<Stage>("all");
  const [query, setQuery] = useState("");
  const list = reviews.read?.kind === "ready" ? reviews.read.value : null;
  const counts = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(STAGE_TEST) as Array<Stage>).map((key) => [key, list?.filter(STAGE_TEST[key]).length ?? 0]),
      ) as Record<Stage, number>,
    [list],
  );
  const shown = useMemo(() => (list ?? []).filter((review) => STAGE_TEST[stage](review) && matches(review, query)), [list, query, stage]);

  return (
    <CiPage
      title="Reviews"
      meta={list ? list.length : undefined}
      data-ci-page="reviews"
      actions={
        orgSegment ? (
          <Button size="sm" className="gap-1.5" render={<Link to="/$org/apps/contract-intelligence/new" params={{ org: orgSegment }} />}>
            <PlusIcon aria-hidden className="size-3.5" />
            New review
          </Button>
        ) : null
      }
    >
      {list && list.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border/50 px-4 py-2 sm:px-6">
          <SegmentedTabs<Stage>
            label="Review stage"
            value={stage}
            onValueChange={setStage}
            className="max-w-full overflow-x-auto"
            tabs={[
              { value: "all", label: "All", count: counts.all },
              { value: "open", label: "Open gaps", count: counts.open },
              { value: "running", label: "In progress", count: counts.running },
              { value: "completed", label: "Complete", count: counts.completed },
              { value: "failed", label: "Not finished", count: counts.failed },
            ]}
          />
          <span className="flex-1" />
          <CiSearch value={query} onChange={setQuery} label="Search reviews" placeholder="Search reviews" />
        </div>
      ) : null}
      {reviews.read === null ? (
        <CiListSkeleton />
      ) : reviews.read.kind !== "ready" ? (
        <CiUnavailable what="Reviews" onRetry={reviews.reload} />
      ) : list && list.length === 0 ? (
        <CiEmpty title="No reviews yet" description="Start one with the documents you want checked and what to check them against.">
          {orgSegment ? (
            <Button size="sm" render={<Link to="/$org/apps/contract-intelligence/new" params={{ org: orgSegment }} />}>
              New review
            </Button>
          ) : null}
        </CiEmpty>
      ) : shown.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-muted-foreground">No reviews match.</p>
      ) : orgSegment ? (
        <div className="divide-y divide-border/50" role="list" aria-label="Reviews">
          {shown.map((review) => (
            <div role="listitem" key={review.id}>
              <CiReviewRow review={review} orgSegment={orgSegment} />
            </div>
          ))}
        </div>
      ) : null}
    </CiPage>
  );
}
